/* eslint-disable no-param-reassign */
/* eslint-disable import/no-cycle */
import axios, { AxiosError } from 'axios';
import qs from 'qs';
import AuthSelectors from 'redux/auth/auth.selectors';
import { store } from 'store';
import { getNewAccessTokenUsingRefreshToken } from 'utils';

const APIService = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

APIService.interceptors.request.use(
  async (request) => {
    const state = store.getState();
    const { accessToken } = AuthSelectors.selectAuth(state);
    request.headers.Authorization = accessToken ? `Bearer ${accessToken}` : null;
    request.baseURL = process.env.REACT_APP_BACKEND_URL;
    request.paramsSerializer = (params) => qs.stringify(params);
    return request;
  },
  (error) => Promise.reject(error),
);

let retryTime = 0;
const RETRY_THRESHOLD = 3;
let isRefreshing = false;
const pendingRequests: {
  reject: (error: AxiosError | null) => void;
  resolve: (token: string | null) => void;
}[] = [];
const resolvePendingRequests = (error: AxiosError | null, token: string | null) => {
  pendingRequests.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });
  pendingRequests.length = 0; // Clear array
};
APIService.interceptors.response.use(
  (res) => {
    retryTime = 0;
    return res;
  },
  (err) => {
    if (err.config && err.response && err.response.status === 401 && retryTime < RETRY_THRESHOLD) {
      retryTime += 1;
      const requestConfig = { ...err.config };
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingRequests.push({ resolve, reject });
        })
          .then((newToken) => {
            requestConfig.headers.Authorization = `Bearer ${newToken}`;
            return APIService(requestConfig);
          })
          .catch((error) => Promise.reject(error));
      }
      isRefreshing = true;
      return new Promise((resolve, reject) =>
        getNewAccessTokenUsingRefreshToken()
          .then((response) => {
            const newToken = response?.data?.accessToken;
            requestConfig.headers.Authorization = newToken ? `Bearer ${newToken}` : null;
            if (pendingRequests.length > 0 && newToken) {
              resolvePendingRequests(null, newToken);
            }
            resolve(APIService(requestConfig));
          })
          .catch((error) => {
            resolvePendingRequests(error, null);
            reject(error);
          })
          .finally(() => {
            isRefreshing = false;
          }),
      );
    }
    const errorMessage = err.response?.data?.message;
    if (errorMessage) {
      err.message = errorMessage;
    }
    return Promise.reject(err);
  },
);

export default APIService;
